
import searchService from "@/services/search.service"
import structuredDataJob from "@/mixins/structuredDataJob"
import breadcrumb from "@/mixins/breadcrumb"

import dates from "@/mixins/dates"
import jobInterest from "@/mixins/job/jobInterest"
import { APPRENTICESHIP, TRAINEESHIP } from "@/enums/jobtypes"
import mapping, { crafts } from "@/helpers/mapping"
const MAP_CRAFT_VALUE = mapping.mapText(crafts)
import { routeToJob } from "@/helpers/routing"

import { mdiCloudUpload } from "@mdi/js"

export default {
    name: "JobPage",
    layout: "modern",
    components: {
        Modal: () => import("@/components/base/modal/Modal"),
        ChatContainer: () => import("@/components/chat/ChatContainer"),
        ChatForm: () => import("@/components/application/interest/ChatForm"),
        ChatFormSuccess: () => import("@/components/chat/ChatFormSuccess"),
        JobPageAusbildung: () =>
            import("@/components/job/jobpage/JobPageAusbildung"),
    },
    mixins: [breadcrumb, structuredDataJob, dates, jobInterest],
    meta: {
        toolbar: {
            hidden: true,
            hideBottomNav: true,
            detailType: "job",
        },
        likeable: true,
        shareable: true,
        hideBackBtn: true,
        hideBottomNav: true,
        footer: {
            hidden: true,
            bottomPadding: 92,
        },
        survey: {
            hidden: true,
        },
    },
    async fetch() {
        if (!this.jobLoaded) {
            await this.$store.dispatch("job/getJobBySlug", {
                slug: this.$route.params.jobSlug,
            })
            this.displayedJob = this.$store.getters["job/displayedJob"]
            this.jobLoaded = true
        }
        if ("ref" in this.$route.query) {
            let meta = this.$store.getters["meta/meta"]
            meta.toolbar.hideBackBtn = true
            this.$store.dispatch("meta/setMeta", meta)
        }
        const job = this.displayedJob
        const isApprenticeship = job.type_id === 1
        const channel = this.$store.getters["search/channel"]
        /** Defines route object to show more recommendations
         * if own-jobs: business-profile
         * else overview w/ lat+lng
         * else no "show more" card
         */
        const businessAddress = job.business_site?.address
        if (this.featureOwnJobs) {
            this.recommendationsRoute = {
                name: "betriebe-slug",
                params: { slug: job.business.slug },
            }
        }
        if (this.featureOtherJobs) {
            if (!businessAddress) return null
            const isMesse =
                this.$store.getters[
                    "routing/previousSearchRoute"
                ]?.name.includes("messe")
            const getRoute = this.$store.getters["search/route"]
            const category = isApprenticeship ? "Ausbildung" : "Praktikum"
            if (isMesse) {
                this.recommendationsRoute = getRoute({ category })
            } else {
                this.recommendationsRoute = getRoute({
                    category,
                    slug: "nearby",
                })
                this.recommendationsRoute.query.category = MAP_CRAFT_VALUE(
                    job.category
                )
                this.recommendationsRoute.query.longitude = businessAddress.lng
                this.recommendationsRoute.query.latitude = businessAddress.lat
            }
        }
        /** Loads recommendations to display below job.
         * Uses flag to determine own-jobs only or general jobs.
         * Includes business-/category- and area-filter
         */
        if (businessAddress) {
            const searchData = {
                type: isApprenticeship ? "apprenticeship" : "traineeship",
                location: {},
                limit: 10,
                exclude_job_slug: job.slug,
                channel: channel || "oabat",
            }
            if (this.featureOwnJobs) {
                searchData.business_slug = job.business.slug
                searchData.limit = 30
            } else {
                searchData.category =
                    job.categories?.length > 0 ? job.categories[0] : null
            }
            if (channel === "oabat") {
                // if channel is not specified, we search around the business, otherwise no location restriction (channel restricts)
                searchData.location.latitude = businessAddress.lat
                searchData.location.longitude = businessAddress.lng
                searchData.distance = 30
            }
            this.recommendations = await searchService.searchJobs(searchData)
        }
    },
    async asyncData({ route, store, error, redirect }) {
        let job = store.getters["job/displayedMinimalJob"]
        let jobLoaded = false
        if (!job || job.slug !== route.params.jobSlug) {
            await store
                .dispatch("job/getJobBySlug", {
                    slug: route.params.jobSlug,
                })
                .catch((errorResponse) => {
                    if (
                        errorResponse?.response?.status === 303 &&
                        errorResponse?.response?.data?.slug
                    ) {
                        redirect(
                            301,
                            routeToJob({
                                slug: errorResponse?.response?.data?.slug,
                                business: {
                                    slug: route.params.slug,
                                },
                            })
                        )
                    } else {
                        error({
                            statusCode: 404,
                            message:
                                "Die Stelle wurde nicht gefunden oder ist leider nicht mehr verfügbar.",
                        })
                    }
                })

            jobLoaded = true
            job = store.getters["job/displayedJob"]
        }
        if (job && route.params.slug !== job.business.slug) {
            return redirect(301, routeToJob(job))
        }

        return {
            displayedJob: job,
            jobLoaded,
        }
    },
    icons: {
        mdiCloudUpload,
    },
    data: () => ({
        jobLoaded: false,
        displayedJob: null,
        recommendations: null,
        recommendationsRoute: null,
    }),
    computed: {
        isApprenticeship() {
            return this.displayedJob.type_id === APPRENTICESHIP
        },
        isTraineeship() {
            return this.displayedJob.type_id === TRAINEESHIP
        },
        isSelfJob() {
            if (!this.$can.business() || !this.displayedJob) return false
            return (
                this.displayedJob.business.slug ===
                this.$auth.user.business.slug
            )
        },
        jobPageV3() {
            if (Number(this.displayedJob?.version) !== 3) {
                this.$sentry.captureMessage(
                    `Job ${this.displayedJob?.id} has no version 3`
                )
            }
            return true
        },
    },
    mounted() {
        if (Object.keys(this.$route.query).length !== 0) {
            if (!this.$route.query.ref) {
                this.$router.replace({ query: null })
            }
        }
    },
    head() {
        const job = this.displayedJob
        if (!job) return
        return {
            title: this.getMetaTitle(job),
            meta: this.getMetaTags(job),
            __dangerouslyDisableSanitizers: ["script"],
            script: this.getScript(job),
        }
    },
}
